import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  applyActiveScrollRefUpdate,
  applyThreadScrollToLatest,
  classifyMessageListChange,
  decideThreadScrollReaction,
  formatNewInboundCount,
  isNearBottom,
  scrollBehaviorFor,
  toMessageScrollIdentities,
  type MessageScrollIdentity,
} from "../conversation-scroll.js";

const LOAD_OLDER_THRESHOLD_PX = 80;

export function useConversationScrollState({
  conversationId,
  messages,
  messagesReady,
  hasOlderMessages,
  loadingOlderMessages,
  onLoadOlderMessages,
}: {
  conversationId: number;
  messages: readonly MessageScrollIdentity[];
  messagesReady: boolean;
  hasOlderMessages: boolean;
  loadingOlderMessages: boolean;
  onLoadOlderMessages: () => boolean;
}): {
  setActiveScrollElement: (
    next: HTMLDivElement | null,
    detaching?: HTMLDivElement | null,
  ) => void;
  handleMessagesScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  isAwayFromBottom: boolean;
  newInboundCount: number;
  formattedInboundCount: string | null;
  scrollToLatest: () => void;
  getActiveScrollElement: () => HTMLDivElement | null;
  markLocalSend: () => void;
  clearLocalSend: () => void;
} {
  const activeScrollElRef = useRef<HTMLDivElement | null>(null);
  const pendingPrependRef = useRef(false);
  const prevScrollHeightRef = useRef<number | null>(null);
  const prevIdentitiesRef = useRef<MessageScrollIdentity[]>([]);
  const prevConversationIdRef = useRef(conversationId);
  const stickToBottomRef = useRef(true);
  const pendingLocalSendRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const wasLoadingOlderRef = useRef(false);
  const identitiesRef = useRef<MessageScrollIdentity[]>([]);
  const [isNearBottomState, setIsNearBottomState] = useState(true);
  const [newInboundCount, setNewInboundCount] = useState(0);

  const identities = messagesReady ? toMessageScrollIdentities(messages) : [];
  identitiesRef.current = identities;
  const identityKey = identities.map((message) => message.messageId).join("\0");

  const setActiveScrollElement = useCallback(
    (next: HTMLDivElement | null, detaching?: HTMLDivElement | null): void => {
      activeScrollElRef.current = applyActiveScrollRefUpdate(
        activeScrollElRef.current,
        next,
        detaching,
      );
    },
    [],
  );

  useLayoutEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      prevConversationIdRef.current = conversationId;
      pendingPrependRef.current = false;
      prevScrollHeightRef.current = null;
      prevIdentitiesRef.current = [];
      stickToBottomRef.current = true;
      pendingLocalSendRef.current = false;
      isNearBottomRef.current = true;
      wasLoadingOlderRef.current = false;
      setIsNearBottomState(true);
      setNewInboundCount(0);
    }

    const el = activeScrollElRef.current;
    const next = identitiesRef.current;

    if (!el || !el.isConnected || !messagesReady) {
      prevIdentitiesRef.current = next;
      wasLoadingOlderRef.current = loadingOlderMessages;
      return;
    }

    const change = classifyMessageListChange(prevIdentitiesRef.current, next);
    const reaction = decideThreadScrollReaction({
      change,
      isNearBottom: isNearBottomRef.current,
      pendingLocalSend: pendingLocalSendRef.current,
    });

    if (reaction.preservePrepend && pendingPrependRef.current && prevScrollHeightRef.current != null) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      pendingPrependRef.current = false;
      prevScrollHeightRef.current = null;
    } else if (
      pendingPrependRef.current &&
      wasLoadingOlderRef.current &&
      !loadingOlderMessages
    ) {
      pendingPrependRef.current = false;
      prevScrollHeightRef.current = null;
    }

    if (stickToBottomRef.current || reaction.followBottom) {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const behavior = stickToBottomRef.current
        ? "auto"
        : scrollBehaviorFor({
            intent: pendingLocalSendRef.current ? "localSend" : "autoFollow",
            reducedMotion,
          });
      applyThreadScrollToLatest(el, behavior);
      stickToBottomRef.current = false;
      isNearBottomRef.current = true;
      setIsNearBottomState(true);
    }

    if (reaction.resetInboundCount) {
      setNewInboundCount(0);
    } else if (reaction.inboundIncrement > 0) {
      setNewInboundCount((count) => count + reaction.inboundIncrement);
    }

    if (reaction.clearLocalSend) {
      pendingLocalSendRef.current = false;
    }

    wasLoadingOlderRef.current = loadingOlderMessages;
    prevIdentitiesRef.current = next;
  }, [conversationId, identityKey, loadingOlderMessages, messagesReady]);

  const syncNearBottom = useCallback((el: HTMLDivElement): void => {
    const near = isNearBottom(el);
    if (near !== isNearBottomRef.current) {
      isNearBottomRef.current = near;
      setIsNearBottomState(near);
    }
    if (near) {
      setNewInboundCount(0);
    }
  }, []);

  const handleMessagesScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>): void => {
      const el = event.currentTarget;
      if (el !== activeScrollElRef.current) {
        return;
      }
      syncNearBottom(el);

      if (!hasOlderMessages || loadingOlderMessages || !messagesReady) {
        return;
      }
      if (el.scrollTop > LOAD_OLDER_THRESHOLD_PX) return;
      if (pendingPrependRef.current) return;
      pendingPrependRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      if (!onLoadOlderMessages()) {
        pendingPrependRef.current = false;
        prevScrollHeightRef.current = null;
      }
    },
    [hasOlderMessages, loadingOlderMessages, messagesReady, onLoadOlderMessages, syncNearBottom],
  );

  const scrollToLatest = useCallback((): void => {
    const el = activeScrollElRef.current;
    if (!el || !el.isConnected) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    applyThreadScrollToLatest(
      el,
      scrollBehaviorFor({ intent: "user", reducedMotion }),
    );
    isNearBottomRef.current = true;
    setIsNearBottomState(true);
    setNewInboundCount(0);
  }, []);

  const markLocalSend = useCallback((): void => {
    pendingLocalSendRef.current = true;
  }, []);

  const clearLocalSend = useCallback((): void => {
    pendingLocalSendRef.current = false;
  }, []);

  return {
    setActiveScrollElement,
    handleMessagesScroll,
    isAwayFromBottom: messagesReady && identities.length > 0 && !isNearBottomState,
    newInboundCount,
    formattedInboundCount: formatNewInboundCount(newInboundCount),
    scrollToLatest,
    getActiveScrollElement: () => activeScrollElRef.current,
    markLocalSend,
    clearLocalSend,
  };
}
